import { DocContent } from '../docsData';

const doc: DocContent = {
  title: 'Ellie Invoke API',
  content: `
The Ellie Invoke API lets you call Ellie programmatically — from another agent, a script, or any backend service — and get a structured JSON response instead of a streaming chat. Ellie runs to completion (tool calls and all) and returns her final answer along with references to any analyses or artifacts she created.

---

## Invoke Ellie

\`\`\`
POST /api/v1/ellie
\`\`\`

Runs Ellie (or another agent type) synchronously. The request blocks until Ellie finishes all tool calls and produces her final response.

### Request

\`\`\`bash
curl -X POST "https://app.pliable.co/api/v1/ellie" \\
  -H "Content-Type: application/json" \\
  -H "Authorization: Bearer YOUR_ACCESS_TOKEN" \\
  -d '{
    "prompt": "What were total sales last month by region?",
    "agent_type": "ellie"
  }'
\`\`\`

| Field | Type | Required | Description |
|---|---|---|---|
| \`prompt\` | string | Yes (or \`messages\`) | The question or instruction for Ellie. |
| \`messages\` | array | Yes (or \`prompt\`) | Full message history in \`[{role, content}]\` format. Use for multi-turn conversations. |
| \`thread_id\` | string | No | Thread ID for conversation continuity. Auto-generated if omitted. |
| \`agent_type\` | string | No | Agent to use. Defaults to \`"ellie"\`. Other options: \`"analyst"\`, \`"build_chart"\`. |
| \`model\` | string | No | LLM model override. Defaults to the tenant's configured Ellie model. |
| \`verbosity\` | string | No | \`"LOW"\`, \`"MEDIUM"\`, or \`"HIGH"\`. Controls how much Ellie narrates her process. |
| \`reasoning\` | string | No | Reasoning effort level for the LLM. |

> **Note:** When using \`prompt\`, it is sent as a single user message. Use \`messages\` when you need to pass conversation history or multi-turn context.

### Response

\`\`\`json
{
  "thread_id": "a1b2c3d4-...",
  "response": "Total sales last month were $1.2M. The West region led with $450K, followed by...",
  "tool_calls": [
    {
      "id": "call_abc123",
      "name": "generate_and_run_analysis",
      "args": {
        "name": "Total Sales by Region - Last Month",
        "metrics": ["Total Sales"],
        "dimensions": ["Region"],
        "where_clauses": [],
        "visualization_type": "BAR",
        "date_range_start": "2026-06-01",
        "date_range_end": "2026-06-30"
      },
      "result": {
        "analysis_id": "667a1b2c3d4e5f..."
      }
    }
  ],
  "analyses": [
    {
      "analysis_id": "667a1b2c3d4e5f...",
      "data_url": "/api/v1/analyses/667a1b2c3d4e5f.../data"
    }
  ],
  "artifacts": []
}
\`\`\`

| Field | Description |
|---|---|
| \`thread_id\` | Conversation thread ID. Pass this back in subsequent requests for multi-turn conversations. |
| \`response\` | Ellie's final text response with her analysis and interpretation. |
| \`tool_calls\` | Every tool call Ellie made during the run. Each entry includes the tool \`name\`, the \`args\` she passed, and a compact \`result\`. For \`generate_and_run_analysis\`, the result contains just the \`analysis_id\` — fetch the full data via the \`data_url\`. |
| \`analyses\` | Convenience list of all analyses created, with URLs to fetch their data. |
| \`artifacts\` | Any markdown artifacts (reports, memos) Ellie created, with URLs to fetch them. |

> **Tip:** Analysis data and artifact content are returned as URL references, not inline, to keep the response compact. Use the URLs to fetch the full data when needed.

---

## Get Analysis Data

\`\`\`
GET /api/v1/analyses/{analysis_id}/data
\`\`\`

Returns the visualization-ready data for an analysis using its saved default settings (date range, filters, sort order). This is the companion endpoint to the invoke response's \`data_url\` — call it to retrieve the actual data behind an analysis Ellie created.

### Request

\`\`\`bash
curl "https://app.pliable.co/api/v1/analyses/667a1b2c3d4e5f.../data" \\
  -H "Authorization: Bearer YOUR_ACCESS_TOKEN"
\`\`\`

| Parameter | Type | Required | Description |
|---|---|---|---|
| \`analysis_id\` | path | Yes | The analysis ID from the invoke response. |
| \`mode\` | query | No | Pass \`?mode=generic\` for a format-agnostic response (no chart library specifics). |

### Response

\`\`\`json
{
  "record": {
    "data": [...],
    "categories": [...],
    "series": [...]
  },
  "node_id": "node_abc123",
  "last_refreshed": "2026-07-08T14:30:00Z",
  "upstream_source_ids": ["src_1", "src_2"]
}
\`\`\`

| Field | Description |
|---|---|
| \`record\` | The visualization configuration including raw data, categories, and series. Shape depends on the analysis's \`visualization_type\`. |
| \`node_id\` | The pipeline node that served this data (may be a dashboard superset node). |
| \`last_refreshed\` | When the underlying data was last built. |
| \`upstream_source_ids\` | The data source IDs that feed into this analysis. |

> **Note:** This endpoint uses the analysis's own saved settings. To query with custom parameters (different filters, date range, pagination), use the existing \`POST /api/v1/analyses/data\` endpoint instead.

---

## Fetch an Artifact

\`\`\`
GET /api/v1/ellieartifacts/{artifact_id}
\`\`\`

Returns the full artifact record, including the markdown content. Use the \`url\` from the invoke response's \`artifacts\` array.

### Response

\`\`\`json
{
  "record": {
    "id": "667b...",
    "name": "Q2 Sales Analysis Report",
    "description": "Comprehensive breakdown of Q2 performance by region",
    "markdown": "> **Ellie:** Here is your Q2 sales report...\\n\\n## 1. Overview\\n...",
    "created_at": "2026-07-08T14:35:00Z"
  }
}
\`\`\`

---

## Agent-to-Agent Behavior

When called via the invoke endpoint, Ellie runs in **agent-to-agent mode**. This changes her behavior:

- **No clarifying questions.** Ellie makes her best judgment instead of asking for clarification.
- **No confirmation prompts.** She won't ask "Does this match what you'd expect?"
- **No follow-up suggestions.** She answers directly without offering next steps.
- **Concise output.** She leads with data and interpretation, skipping conversational pleasantries.
- **Still uses the Knowledge Base.** She checks existing knowledge before running queries and saves new learnings when applicable.

### User Data Restrictions

If the authenticated user has account-level dimension filters (e.g., data scoped to a specific partner) or blacklisted metrics, those restrictions are automatically enforced on every query Ellie runs. Ellie is aware of these restrictions and will communicate them rather than silently presenting filtered data as if it were unfiltered.

### Superset Optimization

When a dashboard superset exists that covers the metrics and dimensions Ellie is querying, the invoke endpoint automatically skips the expensive per-analysis build and reads directly from the superset. This can reduce response times significantly for tenants with dashboard supersets enabled.

---

## Multi-Turn Conversations

To continue a conversation, pass the \`thread_id\` from the previous response:

\`\`\`bash
curl -X POST "https://app.pliable.co/api/v1/ellie" \\
  -H "Content-Type: application/json" \\
  -H "Authorization: Bearer YOUR_ACCESS_TOKEN" \\
  -d '{
    "prompt": "Break that down by product category too",
    "thread_id": "a1b2c3d4-...",
    "agent_type": "ellie"
  }'
\`\`\`

Ellie retains the full conversation context from the thread, so she knows what "that" refers to.
`,
};

export default doc;
